import React, { useState } from "react";
import { emailRegex, nameRegex, passwordRegex } from "../../constants";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login } from "../auth/authSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../components/Loading";
import { NavLink } from "react-router-dom";
import api from "../api";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  // Sign Up States
  const [signUpName, setSignUpName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpErrors, setSignUpErrors] = useState({});

  // Sign In States
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signInErrors, setSignInErrors] = useState({});

  // Toggle Password Visibility
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling that might cause page refresh

    const errors = {};
    setLoading(true);

    if (!signUpName.trim() || !nameRegex.test(signUpName)) {
      errors.name = "Please enter a valid name (letters and spaces only)";
    }
    if (!signUpEmail.trim() || !emailRegex.test(signUpEmail)) {
      errors.email = "Please enter a valid email";
    }
    if (!signUpPassword || !passwordRegex.test(signUpPassword)) {
      errors.password =
        "Password must be at least 8 characters long and contain one letter and one number";
    }

    setSignUpErrors(errors);
    if (Object.keys(errors).length === 0) {
      try {
        const data = await api.request("/admin", {
          method: "POST",
          body: {
            name: signUpName,
            email: signUpEmail,
            password: signUpPassword,
          },
        });
        if (data && data.message) {
          toast.success(`✅ Account created successfully! ${data.message}`, {
            autoClose: 5000,
          });
          // Clear form and errors after successful signup
          setSignUpName("");
          setSignUpEmail("");
          setSignUpPassword("");
          setSignUpErrors({}); // Clear any validation errors

          // Show additional success message
          setTimeout(() => {
            toast.info("🎉 You can now sign in with your new account!", {
              autoClose: 4000,
            });
          }, 1000);
        } else {
          toast.error("❌ Signup failed - Please try again", {
            autoClose: 5000,
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Signup error:", error);

        // Enhanced error handling for signup
        let errorMessage =
          error.message || "An error occurred. Please try again.";
        let errorType = "Signup Error";

        if (
          error.message.includes("already exists") ||
          error.message.includes("conflict") ||
          error.message.includes("duplicate")
        ) {
          errorType = "Account Conflict";
          toast.error(`🚫 ${errorType}: ${errorMessage}`, {
            autoClose: 7000,
          });
        } else if (
          error.message.includes("validation failed") ||
          error.message.includes("Validation Error")
        ) {
          errorType = "Validation Error";
          // Extract specific validation message if available
          const validationMessage = error.message.includes(":")
            ? error.message.split(":").slice(1).join(":").trim()
            : error.message;
          toast.error(`⚠️ ${errorType}: ${validationMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("invalid") ||
          error.message.includes("format")
        ) {
          errorType = "Input Error";
          toast.error(`📝 ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("unauthorized") ||
          error.message.includes("forbidden") ||
          error.message.includes("access denied")
        ) {
          errorType = "Authorization Error";
          toast.error(`🔐 ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("code") ||
          error.message.includes("invitation") ||
          error.message.includes("token")
        ) {
          errorType = "Code Error";
          toast.error(`🎫 ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("Network") ||
          error.message.includes("fetch") ||
          error.message.includes("connection")
        ) {
          errorType = "Network Error";
          errorMessage =
            "Unable to connect to server. Please check your internet connection and try again.";
          toast.error(`🌐 ${errorType}: ${errorMessage}`, {
            autoClose: 8000,
          });
        } else if (
          error.message.includes("timeout") ||
          error.message.includes("took too long")
        ) {
          errorType = "Timeout Error";
          toast.error(`⏰ ${errorType}: Request timed out. Please try again.`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("Server Error") ||
          error.message.includes("Internal") ||
          error.message.includes("500")
        ) {
          errorType = "Server Error";
          errorMessage =
            "Server encountered an error. Please try again in a few moments.";
          toast.error(`🚨 ${errorType}: ${errorMessage}`, {
            autoClose: 8000,
          });
        } else if (
          error.message.includes("required") ||
          error.message.includes("missing")
        ) {
          errorType = "Required Field Error";
          toast.error(`⚠️ ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("rate limit") ||
          error.message.includes("too many")
        ) {
          errorType = "Rate Limit Error";
          toast.error(
            `🚦 ${errorType}: Too many attempts. Please wait and try again.`,
            {
              autoClose: 8000,
            }
          );
        } else {
          // Generic error handling
          toast.error(`❌ ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        }

        // Show additional help message for persistent errors
        setTimeout(() => {
          toast.info(
            "💡 If this error persists, please contact support or try refreshing the page.",
            {
              autoClose: 6000,
            }
          );
        }, 2000);

        setLoading(false);
      }
    } else {
      // Show validation errors in toast
      const errorMessages = Object.values(errors);
      toast.error(`⚠️ Please fix the following: ${errorMessages.join(", ")}`, {
        autoClose: 6000,
      });
    }
    setLoading(false);
  };

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling that might cause page refresh

    const errors = {};
    setLoading(true);

    if (!signInEmail.trim() || !emailRegex.test(signInEmail)) {
      errors.email = "Please enter a valid email";
    }
    if (!signInPassword || !passwordRegex.test(signInPassword)) {
      errors.password = "Invalid password";
    }

    setSignInErrors(errors);
    if (Object.keys(errors).length === 0) {
      try {
        const data = await api.login(signInEmail, signInPassword);
        if (data && data.sessionId) {
          // First dispatch the login action
          dispatch(login({ sessionId: data.sessionId, admin: data.admin }));

          toast.success("🎉 Login successful! Redirecting to dashboard...", {
            autoClose: 3000,
          });

          // Clear form and errors after successful login
          setSignInEmail("");
          setSignInPassword("");
          setSignInErrors({});

          // Wait for Redux state to be updated, then navigate
          setTimeout(() => {
            navigate("/home", { replace: true });
          }, 200);
        } else {
          toast.error("❌ Login failed - Invalid credentials", {
            autoClose: 5000,
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Login error:", error);

        // Enhanced error handling for login
        let errorMessage = error.message || "Invalid email or password";
        let errorType = "Login Error";

        if (
          error.message.includes("don't match") ||
          error.message.includes("unauthorized") ||
          error.message.includes("invalid credentials") ||
          error.message.includes("authentication failed")
        ) {
          errorType = "Authentication Error";
          // Use the exact error message from the server if it contains "don't match"
          if (error.message.includes("don't match")) {
            errorMessage = error.message;
          } else {
            errorMessage =
              "Invalid email or password. Please check your credentials and try again.";
          }
          toast.error(`🔐 ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("Session expired") ||
          error.message.includes("session expired") ||
          error.message.includes("Session") ||
          error.message.includes("token expired") // Keep for backward compatibility
        ) {
          errorType = "Session Error";
          toast.error(
            `⏰ ${errorType}: Your session has expired. Please login again.`,
            {
              autoClose: 6000,
            }
          );
        } else if (
          error.message.includes("account") &&
          (error.message.includes("locked") ||
            error.message.includes("disabled"))
        ) {
          errorType = "Account Error";
          toast.error(`🚫 ${errorType}: ${errorMessage}`, {
            autoClose: 8000,
          });
        } else if (
          error.message.includes("validation failed") ||
          error.message.includes("Validation Error")
        ) {
          errorType = "Validation Error";
          const validationMessage = error.message.includes(":")
            ? error.message.split(":").slice(1).join(":").trim()
            : error.message;
          toast.error(`⚠️ ${errorType}: ${validationMessage}`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("Network") ||
          error.message.includes("fetch") ||
          error.message.includes("connection")
        ) {
          errorType = "Network Error";
          errorMessage =
            "Unable to connect to server. Please check your internet connection and try again.";
          toast.error(`🌐 ${errorType}: ${errorMessage}`, {
            autoClose: 8000,
          });
        } else if (
          error.message.includes("timeout") ||
          error.message.includes("took too long")
        ) {
          errorType = "Timeout Error";
          toast.error(`⏰ ${errorType}: Request timed out. Please try again.`, {
            autoClose: 6000,
          });
        } else if (
          error.message.includes("Server Error") ||
          error.message.includes("Internal") ||
          error.message.includes("500")
        ) {
          errorType = "Server Error";
          errorMessage =
            "Server encountered an error. Please try again in a few moments.";
          toast.error(`🚨 ${errorType}: ${errorMessage}`, {
            autoClose: 8000,
          });
        } else if (
          error.message.includes("rate limit") ||
          error.message.includes("too many")
        ) {
          errorType = "Rate Limit Error";
          toast.error(
            `🚦 ${errorType}: Too many login attempts. Please wait and try again.`,
            {
              autoClose: 8000,
            }
          );
        } else if (
          error.message.includes("forbidden") ||
          error.message.includes("access denied")
        ) {
          errorType = "Access Error";
          toast.error(
            `🚫 ${errorType}: Access denied. Please contact support.`,
            {
              autoClose: 8000,
            }
          );
        } else {
          // Generic error handling
          toast.error(`❌ ${errorType}: ${errorMessage}`, {
            autoClose: 6000,
          });
        }

        // Show additional help message for authentication errors
        if (errorType === "Authentication Error") {
          setTimeout(() => {
            toast.info(
              "💡 Forgot your password? Please contact support for assistance.",
              {
                autoClose: 6000,
              }
            );
          }, 2000);
        }

        setLoading(false);
      }
    } else {
      // Show validation errors in toast
      const errorMessages = Object.values(errors);
      toast.error(`⚠️ Please fix the following: ${errorMessages.join(", ")}`, {
        autoClose: 6000,
      });
    }
    setLoading(false);
  };

  if (loading) {
    return <Loading currentPage={"/login"} />;
  }

  return (
    <>
      <NavLink to="/" className="fixed top-4 left-4 group">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-gray-900 cursor-pointer"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        <span className="absolute top-full left-0 mt-2 hidden group-hover:block bg-gray-900 text-white text-xs p-1 rounded">
          Form
        </span>
      </NavLink>

      {/* Header with Tribco Logo */}
      <div className="bg-white flex justify-center pt-8 pb-6">
        <img src="/tribco-logo.png" alt="Tribco Logo" className="h-20 w-auto" />
      </div>

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex w-full max-w-4xl bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Sign Up Section */}
          <div className="w-1/2 p-8 border-r border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Sign Up
            </h2>
            <form onSubmit={handleSignUpSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="signup-name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  placeholder="Your Name"
                  className={`mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                    signUpErrors.name ? "border-red-500" : ""
                  }`}
                  value={signUpName}
                  onChange={(e) => setSignUpName(e.target.value)}
                />
                {signUpErrors.name && (
                  <p className="mt-1 text-xs text-red-500">
                    {signUpErrors.name}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="signup-email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder="Your Email"
                  className={`mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                    signUpErrors.email ? "border-red-500" : ""
                  }`}
                  value={signUpEmail}
                  onChange={(e) => setSignUpEmail(e.target.value)}
                />
                {signUpErrors.email && (
                  <p className="mt-1 text-xs text-red-500">
                    {signUpErrors.email}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="signup-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="relative mt-1">
                  <input
                    id="signup-password"
                    type={showSignUpPassword ? "text" : "password"}
                    placeholder="Your Password"
                    className={`mt-1 block w-full rounded-md border border-gray-300 p-2 pr-10 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                      signUpErrors.password ? "border-red-500" : ""
                    }`}
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showSignUpPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.958 9.958 0 012.518-4.123m2.359-2.358A9.965 9.965 0 0112 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 3l18 18"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {signUpErrors.password && (
                  <p className="mt-1 text-xs text-red-500">
                    {signUpErrors.password}
                  </p>
                )}
              </div>
              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-950 focus:outline-none"
                >
                  Sign Up
                </button>
              </div>
            </form>
          </div>

          {/* Sign In Section */}
          <div className="w-1/2 p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Sign In
            </h2>
            <form onSubmit={handleSignInSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="signin-email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="signin-email"
                  type="email"
                  placeholder="Your Email"
                  className={`mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                    signInErrors.email ? "border-red-500" : ""
                  }`}
                  value={signInEmail}
                  onChange={(e) => setSignInEmail(e.target.value)}
                />
                {signInErrors.email && (
                  <p className="mt-1 text-xs text-red-500">
                    {signInErrors.email}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="signin-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="relative mt-1">
                  <input
                    id="signin-password"
                    type={showSignInPassword ? "text" : "password"}
                    placeholder="Your Password"
                    className={`mt-1 block w-full rounded-md border border-gray-300 p-2 pr-10 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                      signInErrors.password ? "border-red-500" : ""
                    }`}
                    value={signInPassword}
                    onChange={(e) => setSignInPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignInPassword(!showSignInPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showSignInPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.958 9.958 0 012.518-4.123m2.359-2.358A9.965 9.965 0 0112 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 3l18 18"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {signInErrors.password && (
                  <p className="mt-1 text-xs text-red-500">
                    {signInErrors.password}
                  </p>
                )}
              </div>
              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-950 focus:outline-none"
                >
                  Sign In
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Toast Container for notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={3}
      />
    </>
  );
};

export default LoginPage;
